/**
 * ScoreMethodology — един и същ начин на обяснение за всяка оценка в DHI.
 * Приема `explain` и `contributors` от backend и рендира: приоритетни
 * действия, липсващи данни, принос на всеки фактор и разгъваща се пълна
 * методика (скала, входове, тежести).
 */
import React, { useState } from "react";
import { ChevronDown, ChevronUp, TrendingUp, Info } from "lucide-react";

const BAND_FILL = {
  green: "#22C55E", yellow: "#EAB308", orange: "#F97316", red: "#EF4444",
};

export const BandLine = ({ explain, band, testid }) => {
  const b = (explain?.bands_bg || []).find((x) => x.color === band);
  if (!b) return null;
  return (
    <div className="text-[11px] font-bold" style={{ color: BAND_FILL[b.color] }}
      data-testid={testid || "score-band-line"}>
      {b.range} · {b.label_bg} — {b.meaning_bg}
    </div>
  );
};

export default function ScoreMethodology({
  explain, contributors = [], isDark = false, unit = "точки",
  barScale = 5, title = "Как точно се изчислява и кога е ниска",
  testid = "score-methodology",
}) {
  const [open, setOpen] = useState(false);
  if (!explain) return null;
  const border = isDark ? "border-white/10" : "border-gray-200";
  const isYears = unit === "години";

  return (
    <div data-testid={testid}>
      {explain.raise_bg?.length > 0 && (
        <div className="mt-3" data-testid={`${testid}-raise`}>
          <div className="text-[10px] uppercase tracking-wider font-bold opacity-50 mb-1 inline-flex items-center gap-1">
            <TrendingUp className="w-3 h-3" /> Какво тежи най-много при Вас
          </div>
          <ul className="space-y-0.5">
            {explain.raise_bg.map((x, i) => (
              <li key={i} className="text-[11px] opacity-85">· {x}</li>
            ))}
          </ul>
        </div>
      )}

      {explain.missing_bg?.length > 0 && (
        <div className="mt-2 p-2 rounded-lg bg-amber-500/10 border border-amber-500/30"
          data-testid={`${testid}-missing`}>
          <div className="text-[10px] font-bold text-amber-600 dark:text-amber-300 inline-flex items-center gap-1">
            <Info className="w-3 h-3" /> Оценката е непълна
          </div>
          <ul className="mt-0.5 space-y-0.5">
            {explain.missing_bg.map((x, i) => (
              <li key={i} className="text-[10px] opacity-80">· {x}</li>
            ))}
          </ul>
        </div>
      )}

      {contributors.length > 0 && (
        <div className="mt-4">
          <div className="text-[10px] uppercase tracking-wider font-bold opacity-50 mb-1.5">
            Принос на всеки фактор
            {explain.base !== undefined && ` (база ${explain.base} ${
              isYears ? "години" : "точки"})`}
          </div>
          <div className="space-y-1" data-testid={`${testid}-contributors`}>
            {contributors.map((c, i) => {
              const ratio = c.max_points ? c.points / c.max_points : null;              const good = ratio !== null ? ratio >= 0.7
                : (isYears ? c.points < 0 : c.points > 0);
              const barColor = ratio !== null
                ? (ratio >= 0.7 ? "#22C55E" : ratio >= 0.5 ? "#EAB308" : "#EF4444")
                : (good ? "#22C55E" : "#EF4444");
              const width = Math.min(100, Math.abs(c.points) * barScale);
              return (
                <div key={c.key ?? i} className={`p-2 rounded-lg border ${border}`}
                  data-testid={`${testid}-factor-${c.key ?? i}`}>
                  <div className="flex items-start justify-between gap-2">
                    <span className="text-[11px] min-w-0">
                      {c.label_bg}
                      {c.weight_pct !== undefined && (
                        <span className="opacity-55"> · тежест {c.weight_pct}%
                          {c.score !== undefined && ` · ${c.score}/100`}</span>
                      )}
                    </span>
                    <span className={`text-[11px] font-mono font-bold shrink-0 ${
                      good ? "text-emerald-500" : ratio !== null && ratio >= 0.5
                        ? "text-amber-500" : "text-rose-500"}`}>
                      {c.points > 0 ? "+" : "−"}{Math.abs(c.points)}
                      {c.max_points !== undefined && ` / ${c.max_points}`}
                    </span>
                  </div>
                  <div className="mt-1 h-1.5 rounded-full overflow-hidden"
                    style={{ background: isDark ? "rgba(255,255,255,.07)" : "rgba(0,0,0,.05)" }}>
                    <div className="h-full rounded-full"
                      style={{
                        width: `${ratio !== null ? ratio * 100 : width}%`,
                        background: barColor,
                      }} />
                  </div>
                  {c.group && (
                    <div className="text-[9px] opacity-50 mt-0.5">
                      {{
                        prevention: "от превантивните модули",
                        nutrition: "от храненето и профила",
                        dental: "от измерените дентални данни",
                        metabolic: "от метаболизма и начина на живот",
                        component: "под-оценка",
                      }[c.group] || c.group}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      <button onClick={() => setOpen(!open)}
        className="mt-3 text-[11px] font-bold text-cyan-500 inline-flex items-center gap-1"
        data-testid={`${testid}-toggle`}>
        {open ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
        {title}
      </button>
      {open && (
        <div className="mt-2 space-y-3" data-testid={`${testid}-detail`}>
          <p className="text-[11px] opacity-80">{explain.how_bg}</p>

          {explain.bands_bg?.length > 0 && (
            <div>
              <div className="text-[10px] uppercase font-bold opacity-55 mb-1">Скала</div>
              <div className="grid sm:grid-cols-2 gap-1.5">
                {explain.bands_bg.map((b) => (
                  <div key={b.range} className={`p-2 rounded-lg border ${border}`}
                    data-testid={`${testid}-band-${b.color}`}>
                    <div className="text-[11px] font-bold" style={{ color: BAND_FILL[b.color] }}>
                      {b.range} · {b.label_bg}
                    </div>
                    <div className="text-[10px] opacity-75">{b.meaning_bg}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {explain.inputs_bg?.length > 0 && (
            <div>
              <div className="text-[10px] uppercase font-bold opacity-55 mb-1">
                Откъде идват данните
              </div>
              <ul className="space-y-0.5">
                {explain.inputs_bg.map((x, i) => (
                  <li key={i} className="text-[10px] opacity-75">· {x}</li>
                ))}
              </ul>
            </div>
          )}

          {explain.rules_bg?.length > 0 && (
            <div>
              <div className="text-[10px] uppercase font-bold opacity-55 mb-1">
                Тежест на правилата
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
                {explain.rules_bg.map((r, i) => {
                  const minus = r.points.includes("−") || r.points.includes("-");
                  return (
                    <div key={i}
                      className={`flex items-center justify-between gap-2 px-2 py-1 rounded text-[10px] ${
                        minus
                          ? (isDark ? "bg-rose-500/10 text-rose-300" : "bg-rose-50 text-rose-700")
                          : (isDark ? "bg-emerald-500/10 text-emerald-300" : "bg-emerald-50 text-emerald-700")}`}>
                      <span className="min-w-0">{r.label_bg}</span>
                      <span className="font-mono font-bold shrink-0">{r.points}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div className="text-[9px] opacity-55">{explain.note_bg}</div>
        </div>
      )}
    </div>
  );
}
